import {
    Button, Card, Col, ConfigProvider, DatePicker, Divider, Empty, Modal, Row,
    Select, Space, Spin, Table, Tag, Tooltip, Typography
} from "antd";
import PropTypes from "prop-types";
import { createRef, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CheckCircleTwoTone, ReloadOutlined, EyeOutlined,
    FileDoneOutlined, BankOutlined, UserOutlined, TeamOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import frFR from "antd/locale/fr_FR";

import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import { frenchDate } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import { StatCard } from "../../components/statistics/StatCard";
import { COLORS } from "../../components/ui/colors";
import BorderauReversementCompagnie from "./docs/BorderauReversementCompagnie";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Types d'apporteur (bénéficiaire) — identique aux commissions générées
const TYPE_LABELS = {
    INTERMEDIAIRE: { label: "Intermédiaire", color: "purple", icon: <UserOutlined /> },
    COURTIER: { label: "Courtier", color: "geekblue", icon: <BankOutlined /> },
    AGENCE: { label: "Agence", color: "cyan", icon: <BankOutlined /> },
};

// Couleur du tag selon le mode de paiement
const MODE_COLORS = {
    ESPECES: "blue",
    RISTOURNE: "green",
    BANQUE: "gold",
    VIREMENT: "geekblue",
    MOBILE_MONEY: "magenta",
    COMPENSATION: "purple",
};

/** Aperçu imprimable du bordereau de reversement. */
const BordereauVersementPrintable = ({ emissions, draft, numeroBordereau, reverseur }) => {
    const ref = createRef();
    const totalPrimes = emissions.reduce((s, l) => s + (l.montant || 0), 0);

    return (
        <Card
            style={{ borderRadius: 14, marginBottom: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            bodyStyle={{ padding: 16 }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Space wrap>
                    <Tag color="cyan" style={{ borderRadius: 6 }}><BankOutlined /> Reversement en compagnie</Tag>
                    <Text type="secondary">· {emissions.length} règlement(s)</Text>
                    <Tag color="green" style={{ borderRadius: 6 }}>{displayNumbers(Math.trunc(totalPrimes))} FCFA</Tag>
                    {numeroBordereau
                        ? <Tag color="orange" style={{ borderRadius: 6 }}>N° {numeroBordereau}</Tag>
                        : draft && <Tag style={{ borderRadius: 6 }}>Brouillon — n° attribué à la validation</Tag>}
                </Space>
                {/* Aucune impression dans la modale d'aperçu/génération : l'impression se fait
                    uniquement depuis les pages de bordereaux déjà validés. */}
            </div>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 8, overflowX: "auto" }}>
                <BorderauReversementCompagnie ref={ref} emissions={emissions} numeroBordereau={numeroBordereau} reverseur={reverseur} />
            </div>
        </Card>
    );
};

BordereauVersementPrintable.propTypes = {
    emissions: PropTypes.array,
    draft: PropTypes.bool,
    numeroBordereau: PropTypes.string,
    reverseur: PropTypes.string,
};

const VersementsEnCompagnie = () => {
    const navigate = useNavigate();

    const [datas, setDatas] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [dateF, setDateF] = useState([]);
    const [agenceFilter, setAgenceFilter] = useState([]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [generating, setGenerating] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [validated, setValidated] = useState(false);
    const [numeroBordereau, setNumeroBordereau] = useState(null);
    // Période affichée sur le bordereau : celle du filtre si posé, sinon PROPOSÉE d'après le
    // min/max des dates de règlement des lignes sélectionnées. Ajustable dans l'aperçu.
    const [periodeGen, setPeriodeGen] = useState([null, null]);

    // ── Chargement des règlements à reverser de la période ──
    const fetchData = () => {
        setLoadingData(true);
        axiosInstance
            .get(`/api/reglement/a-reverser`, {
                params: {
                    page: 0,
                    size: 5000,
                    search: "",
                    startDate: dateF && dateF[0] ? dateF[0] : null,
                    endDate: dateF && dateF.length > 1 ? dateF[1] : null,
                    compagnie: "",
                },
            })
            .then((res) => {
                setDatas(res.data.content || []);
                setLoadingData(false);
            })
            .catch(() => setLoadingData(false));
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [dateF]);

    // ── Options du filtre par apporteur (agences / intermédiaires / courtiers distincts) ──
    const beneficiairesOptions = useMemo(() => {
        const map = new Map();
        datas.forEach((d) => {
            if (d.beneficiaireCode && !map.has(d.beneficiaireCode)) {
                map.set(d.beneficiaireCode, {
                    label: `${d.beneficiaireNom || d.beneficiaireCode} — ${(TYPE_LABELS[d.beneficiaireType] || {}).label || d.beneficiaireType}`,
                    value: d.beneficiaireCode,
                });
            }
        });
        return Array.from(map.values());
    }, [datas]);

    // ── Filtrage client par apporteur ──
    const filteredDatas = useMemo(() => {
        return datas.filter((d) =>
            (agenceFilter.length === 0 || agenceFilter.includes(d.beneficiaireCode))
        );
    }, [datas, agenceFilter]);

    // La sélection est entièrement manuelle : seules les lignes réellement cochées par
    // l'utilisateur sont prises en compte par les opérations (aperçu / génération). On
    // résout les lignes sélectionnées depuis le jeu de données complet (et non la page
    // courante) pour que la sélection soit conservée d'une page à l'autre et d'un filtre
    // à l'autre — les opérations utilisent donc toujours TOUS les éléments réellement cochés.
    const selectedRows = useMemo(
        () => datas.filter((d) => selectedRowKeys.includes(d.codeId)),
        [datas, selectedRowKeys]
    );

    // ── Indicateurs ──
    const stats = useMemo(() => {
        const montantTotal = filteredDatas.reduce((s, d) => s + (d.montant || 0), 0);
        const primeNetteTotal = filteredDatas.reduce((s, d) => s + ((d.montant || 0) - (d.accessoires || 0) - (d.taxes || 0)), 0);
        const selMontant = selectedRows.reduce((s, d) => s + (d.montant || 0), 0);
        const nbApporteurs = new Set(filteredDatas.map((d) => d.beneficiaireCode)).size;
        return { montantTotal, primeNetteTotal, selMontant, nbApporteurs };
    }, [filteredDatas, selectedRows]);

    // ── « Versé par » : l'apporteur (agence / intermédiaire / courtier) des règlements
    //    sélectionnés. Un seul apporteur (cas d'un utilisateur d'agence, ou filtre sur un
    //    apporteur) → son nom ; plusieurs → mention générique ; aucun → agence de l'utilisateur. ──
    const reverseur = useMemo(() => {
        const noms = Array.from(new Set(selectedRows.map((r) => r.beneficiaireNom).filter(Boolean)));
        if (noms.length === 1) return noms[0];
        if (noms.length > 1) return "Plusieurs apporteurs";
        return LOGGED_USER?.agence?.nom
            || LOGGED_USER?.profil?.organisme?.raisonSociale
            || `${LOGGED_USER?.nom || ""} ${LOGGED_USER?.prenom || ""}`.trim();
    }, [selectedRows]);

    const columns = [
        {
            title: "Apporteur",
            dataIndex: "beneficiaireNom",
            key: "beneficiaireNom",
            render: (_, r) => {
                const t = TYPE_LABELS[r.beneficiaireType] || TYPE_LABELS.AGENCE;
                return (
                    <Space direction="vertical" size={0}>
                        <Text strong style={{ fontSize: 13 }}>{r.beneficiaireNom || "—"}</Text>
                        <Tag color={t.color} style={{ borderRadius: 6, fontSize: 11 }}>{t.icon} {t.label} · {r.beneficiaireCode}</Tag>
                    </Space>
                );
            },
        },
        {
            title: "Assuré",
            dataIndex: "nom",
            key: "nom",
            render: (_, r) => `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`,
        },
        { title: "Police", dataIndex: "numeroPolice", key: "numeroPolice" },
        { title: "Avenant", dataIndex: "avenant", key: "avenant", render: (_, r) => r?.avenant || "AN" },
        {
            // Prime nette = TTC (montant) - accessoires - taxes
            title: "Prime nette", dataIndex: "primeNette", key: "primeNette",
            align: "right", render: (_, r) => displayNumbers((r.montant || 0) - (r.accessoires || 0) - (r.taxes || 0)),
        },
        {
            title: "Montant TTC", dataIndex: "montant", key: "montant",
            align: "right", render: (_, r) => <Text strong>{displayNumbers(r.montant)}</Text>,
        },
        {
            title: "Mode de paiement", dataIndex: "modePaiement", key: "modePaiement",
            render: (_, r) => (r.modePaiement && r.modePaiement !== "-"
                ? <Tag color={MODE_COLORS[r.modePaiement] || "default"}>{r.modePaiement}</Tag>
                : <Tag>NON PAYÉ</Tag>),
        },
        {
            title: "Date règlement", dataIndex: "dateReglement", key: "dateReglement",
            render: (_, r) => (r.modePaiement ? frenchDate(r.dateReglement) : "-"),
        },
        {
            title: "", key: "actions", width: 50,
            render: (_, r) => (
                <Tooltip title="Voir le contrat">
                    <Button size="small" shape="circle" icon={<EyeOutlined />}
                        onClick={() => navigate(`/details-contrat/${r.contratCodeId}`)} />
                </Tooltip>
            ),
        },
    ];

    const periodeLabel = (dateF && dateF[0])
        ? `${dayjs(dateF[0]).format("DD/MM/YYYY")} – ${dateF[1] ? dayjs(dateF[1]).format("DD/MM/YYYY") : "…"}`
        : "Toutes périodes";

    // Étape 1 : aperçu (aucune écriture en base)
    const previsualiser = () => {
        if (selectedRows.length === 0) {
            toast.warn("Sélectionnez au moins un règlement");
            return;
        }
        // Période à afficher : le filtre s'il est posé, sinon proposée d'après le min/max des
        // dates de règlement des lignes sélectionnées (ajustable dans l'aperçu).
        if (dateF && dateF[0] && dateF[1]) {
            setPeriodeGen([dayjs(dateF[0]), dayjs(dateF[1])]);
        } else {
            const dates = selectedRows
                .map((r) => r.dateReglement)
                .filter(Boolean)
                .map((d) => dayjs(d))
                .filter((d) => d.isValid());
            if (dates.length) {
                let min = dates[0], max = dates[0];
                dates.forEach((d) => { if (d.isBefore(min)) min = d; if (d.isAfter(max)) max = d; });
                setPeriodeGen([min, max]);
            } else {
                setPeriodeGen([null, null]);
            }
        }
        setValidated(false);
        setNumeroBordereau(null);
        setResultOpen(true);
    };

    // Étape 2 : valider et générer réellement le bordereau
    const validerEtGenerer = () => {
        setGenerating(true);
        axiosInstance
            .post(`/api/bordereau/versements-en-compagnie`, JSON.stringify(selectedRows), {
                params: {
                    startDate: periodeGen && periodeGen[0] ? periodeGen[0].format("YYYY-MM-DD") : null,
                    endDate: periodeGen && periodeGen[1] ? periodeGen[1].format("YYYY-MM-DD") : null,
                },
            })
            .then((res) => {
                setNumeroBordereau(res.data?.numeroBordereau || null);
                setValidated(true);
                setGenerating(false);
                toast.success("Bordereau de reversement généré avec succès");
                fetchData();
            })
            .catch((err) => {
                console.log(err);
                setGenerating(false);
                toast.error("Erreur lors de la génération du bordereau");
            });
    };

    const rowSelection = {
        selectedRowKeys,
        // preserveSelectedRowKeys : conserve les clés cochées même quand la ligne n'est pas
        // présente sur la page de pagination courante ou est masquée par un filtre.
        preserveSelectedRowKeys: true,
        onChange: (keys) => setSelectedRowKeys(keys),
        // Un règlement NON PAYÉ (sans mode de paiement) n'est pas encaissé : il ne peut pas
        // être reversé en compagnie → sa case à cocher est désactivée. « Tout sélectionner »
        // n'agira donc que sur les règlements réellement payés.
        getCheckboxProps: (record) => ({
            disabled: !(record.modePaiement && record.modePaiement !== "-"),
        }),
        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
    };

    return (
        <div class="layout-wrapper layout-content-navbar">
            <div class="layout-container">
                <SideBar currentMenu={"CAISSE"} />
                <div class="layout-page">
                    <NavBar />
                    <div class="content-wrapper">
                        <div class="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "22px 26px",
                                marginBottom: 18, color: "#fff",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
                            }}>
                                <div>
                                    <Title level={3} style={{ color: "#fff", margin: 0 }}>Versements en compagnie</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Générez le bordereau de reversement des primes aux compagnies sur une période
                                    </Text>
                                </div>
                                <Space wrap>
                                    <Tooltip title="Rafraîchir">
                                        <Button icon={<ReloadOutlined />} onClick={fetchData} />
                                    </Tooltip>
                                </Space>
                            </div>

                            {/* Indicateurs */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                <Col xs={12} md={6}>
                                    <StatCard title="Règlements à reverser" subtitle="période filtrée"
                                        value={`${filteredDatas.length}`} icon="bx bx-list-check"
                                        iconBg={COLORS.primary} gradient={COLORS.primary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Montant TTC total" subtitle="période filtrée"
                                        value={`${displayNumbers(Math.trunc(stats.montantTotal))} F`} icon="bx bx-wallet"
                                        iconBg={COLORS.success} gradient={COLORS.success} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Apporteurs" subtitle="agences / intermédiaires"
                                        value={`${stats.nbApporteurs}`} icon="bx bx-buildings"
                                        iconBg={COLORS.secondary} gradient={COLORS.secondary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Sélection" subtitle={`${selectedRows.length} règlement(s)`}
                                        value={`${displayNumbers(Math.trunc(stats.selMontant))} F`} icon="bx bx-check-square"
                                        iconBg={COLORS.warning} gradient={COLORS.warning} />
                                </Col>
                            </Row>

                            {/* Filtres + action */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={9}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Agence / Intermédiaire / Courtier</Text>
                                        <Select mode="multiple" allowClear style={{ width: "100%" }}
                                            placeholder="Tous les apporteurs"
                                            maxTagCount="responsive" optionFilterProp="label"
                                            value={agenceFilter} onChange={setAgenceFilter}
                                            suffixIcon={<TeamOutlined />}
                                            options={beneficiairesOptions} />
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Période</Text>
                                        <RangePicker
                                            style={{ width: "100%" }}
                                            format="DD/MM/YYYY"
                                            value={dateF && dateF[0] ? [dayjs(dateF[0]), dayjs(dateF[1])] : null}
                                            onChange={(v) => setDateF(v ? [v[0].format("YYYY-MM-DD"), v[1].format("YYYY-MM-DD")] : [])}
                                            placeholder={["Date début", "Date fin"]}
                                        />
                                    </Col>
                                    <Col xs={24} md={8} style={{ textAlign: "right", alignSelf: "flex-end" }}>
                                        <Button type="primary" size="large" icon={<EyeOutlined />}
                                            onClick={previsualiser}
                                            disabled={selectedRows.length === 0}
                                            style={{ background: COLORS.gradient, border: "none", borderRadius: 10, fontWeight: 600 }}>
                                            Aperçu du bordereau ({selectedRows.length})
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 8 }}>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            rowSelection={rowSelection}
                                            columns={columns}
                                            dataSource={filteredDatas}
                                            rowKey="codeId"
                                            size="middle"
                                            locale={{ emptyText: <Empty description="Aucun règlement à reverser sur la période" /> }}
                                            pagination={{ showSizeChanger: true, defaultPageSize: 10, showTotal: (t) => `${t} règlement(s)` }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>
                        <Footer />
                        <div class="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {/* Modale aperçu / génération du bordereau */}
            <Modal
                title={
                    <Space>
                        <FileDoneOutlined style={{ color: COLORS.primary }} />
                        {validated ? "Bordereau de reversement généré" : "Aperçu du bordereau de reversement"}
                    </Space>
                }
                centered
                open={resultOpen}
                onCancel={() => setResultOpen(false)}
                width={1250}
                zIndex={2000}
                footer={
                    validated
                        ? [
                            <Button key="close" type="primary" icon={<CheckCircleTwoTone />} onClick={() => setResultOpen(false)}>
                                Terminer
                            </Button>,
                        ]
                        : [
                            <Button key="cancel" onClick={() => setResultOpen(false)}>Annuler</Button>,
                            <Button key="validate" type="primary" icon={<FileDoneOutlined />}
                                loading={generating} onClick={validerEtGenerer}
                                style={{ background: COLORS.gradient, border: "none" }}>
                                Valider et générer
                            </Button>,
                        ]
                }
            >
                <div style={{
                    background: validated ? "#f6ffed" : "#fffbe6",
                    border: `1px solid ${validated ? "#b7eb8f" : "#ffe58f"}`,
                    borderRadius: 8, padding: "8px 12px",
                }}>
                    <Text>
                        {validated
                            ? <>✅ Bordereau <b>généré et enregistré</b>. Il est imprimable ci-dessous.</>
                            : <>👁️ <b>Aperçu</b> — aucun bordereau n'est encore enregistré. Vérifiez le contenu, puis cliquez sur <b>« Valider et générer »</b>. Pour exclure des règlements, fermez et décochez-les dans le tableau.</>}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">{selectedRows.length} règlement(s)</Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">Versé par : <b>{reverseur || "—"}</b></Text>
                    </div>
                    {!validated && (
                        <div style={{ marginTop: 8 }}>
                            <Text strong>Période affichée sur le bordereau : </Text>
                            <RangePicker
                                format="DD/MM/YYYY"
                                value={periodeGen && periodeGen[0] ? [periodeGen[0], periodeGen[1]] : null}
                                onChange={(v) => setPeriodeGen(v && v[0] ? [v[0], v[1]] : [null, null])}
                                allowClear
                                style={{ marginLeft: 4 }}
                            />
                            <div style={{ marginTop: 2 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {dateF && dateF[0]
                                        ? "Reprise du filtre appliqué — ajustable."
                                        : "Aucun filtre posé : période proposée d'après les dates de règlement. Ajustable."}
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
                <Divider style={{ margin: "12px 0" }} />
                <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                    <BordereauVersementPrintable
                        emissions={selectedRows}
                        draft={!validated}
                        numeroBordereau={numeroBordereau}
                        reverseur={reverseur}
                        periodeDebut={periodeGen && periodeGen[0] ? periodeGen[0].format("YYYY-MM-DD") : null}
                        periodeFin={periodeGen && periodeGen[1] ? periodeGen[1].format("YYYY-MM-DD") : null}
                    />
                </div>
            </Modal>

            <div class="layout-overlay layout-menu-toggle"></div>
            <div class="drag-target"></div>
        </div>
    );
};

export default VersementsEnCompagnie;
